import { useState } from "react";
import { Link, useMatch, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CAlert,
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CSpinner,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilBook } from "@coreui/icons";
import {
  annualSavingsPct,
  parseBillingCycle,
  priceForCycle,
  useTradingStrategies,
  useTradingStrategy,
  type BillingCycle,
  isFreeStrategy,
} from "../../api/hooks/useTradingStrategies";
import {
  canExtendYear,
  canUpgradeToYearly,
  customerHasActiveSubscription,
  useMySubscriptions,
} from "../../api/hooks/useStrategySubscriptions";
import {
  membershipsIncluding,
  useMyMemberships,
} from "../../api/hooks/useMemberships";
import { useCartStore, type StrategyCartItem } from "../../state/cartStore";
import { useToast } from "../../context/useToast";
import { helpArticlePath, routePaths } from "../../routes/route-paths";
import {
  StrategyFigures,
  TradingStrategyInsights,
} from "./components/TradingStrategyInsights";
import { StrategyColumnLink, StrategyColumnLinks } from "./components/StrategyColumnLink";
import { StrategyFeedbackLinks } from "./components/StrategyFeedbackLinks";
import { StrategyTraitFacts } from "./components/StrategyTraitFacts";
import { StrategyPriceHeadline } from "./components/StrategyPrice";
import { StrategySideTabs } from "./components/StrategySideTabs";
import { sidesOf } from "./strategySides";
import { CartHoldsUpgradeDialog } from "./components/CartHoldsUpgradeDialog";
import { UpgradeOnItsOwnDialog } from "./components/UpgradeOnItsOwnDialog";
import { ExtendOnItsOwnDialog } from "./components/ExtendOnItsOwnDialog";
import {
  offersForLine,
  usableOffers,
  useUsableOffers,
} from "../../api/hooks/useOffers";
import { savingOnLine } from "../../lib/offer-savings";
import { formatPriceUsd } from "../../lib/money";
import { TradingStrategyStatus } from "../../api/hooks/useTradingStrategies";

export function TradingStrategyDetailsCoreui() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const fromMyStrategies = useMatch(routePaths.myStrategyDetails) !== null;
  const { data: strategy, isLoading, error } = useTradingStrategy(id);
  const { data: catalogue = [] } = useTradingStrategies();
  const addStrategy = useCartStore((s) => s.addStrategy);
  const { showSuccess } = useToast();
  const removeStrategy = useCartStore((s) => s.removeStrategy);
  const updateStrategyBillingCycle = useCartStore((s) => s.updateStrategyBillingCycle);
  const strategyItems = useCartStore((s) => s.strategyItems);
  const cartMembershipItems = useCartStore((s) => s.membershipItems);
  const cartAddOnItems = useCartStore((s) => s.addOnItems);
  const cartUpgrade = useCartStore((s) => s.upgrade);
  const startUpgrade = useCartStore((s) => s.startUpgrade);
  const cartExtension = useCartStore((s) => s.extension);
  const startExtension = useCartStore((s) => s.startExtension);
  const [heldBack, setHeldBack] = useState<StrategyCartItem | null>(null);
  const [askingToEmptyCart, setAskingToEmptyCart] = useState(false);
  const [askingToEmptyCartForExtension, setAskingToEmptyCartForExtension] = useState(false);
  const offers = usableOffers(useUsableOffers().data);
  const subscriptions = useMySubscriptions();
  const subscriptionItems = subscriptions.data ?? [];
  const memberships = useMyMemberships();
  const membershipItems = memberships.data ?? [];
  const [searchParams] = useSearchParams();
  // Seeded from the link that got here, so a card clicked while the catalogue was showing annual
  // prices opens on the same figure rather than silently reverting to monthly. Only the starting
  // point: the buttons below take over from here, and the URL is not rewritten as they are used.
  const [pendingCycle, setPendingCycle] = useState<BillingCycle>(() =>
    parseBillingCycle(searchParams.get("cycle")),
  );

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (error || !strategy) {
    return (
      <div>
        <CAlert color="warning">
          Strategy not found.{" "}
          <Link to={routePaths.strategies}>Back to strategies</Link>
        </CAlert>
      </div>
    );
  }

  const cartItem = strategyItems.find((i) => i.strategyId === strategy.id);
  const inCart = cartItem !== undefined;
  const subscribed = customerHasActiveSubscription(
    strategy.id,
    subscriptionItems,
  );
  const includedIn = membershipsIncluding(strategy.id, membershipItems);
  const subscriptionStateUnavailable =
    subscriptions.isLoading ||
    Boolean(subscriptions.error) ||
    memberships.isLoading ||
    Boolean(memberships.error);
  const isPublic = strategy.status === TradingStrategyStatus.Published;
  const selectedCycle: BillingCycle = cartItem?.billingCycle ?? pendingCycle;
  const selectedPrice = priceForCycle(strategy, selectedCycle);
  const savings = annualSavingsPct(strategy);
  const sides = sidesOf(strategy, catalogue);
  const hasSideTabs = sides.length > 1;
  const heldOtherSide = sides.find(
    (side) =>
      side.id !== strategy.id &&
      customerHasActiveSubscription(side.id, subscriptionItems),
  );
  const heldOtherSideWords = heldOtherSide
    ? t(`tradingStrategies:sides.${heldOtherSide.direction ?? 0}`, {
        defaultValue: heldOtherSide.publicName,
      }).toLocaleLowerCase()
    : "";

  // Deliberately a copy of the subscription page's Upgrade button - change both together. Only the
  // live row renews, so ended rows sharing the strategy never offer it.
  const heldSubscription = subscriptionItems.find(
    (s) => s.strategy.id === strategy.id && s.renewsOn !== null,
  );
  const canUpgrade =
    heldSubscription !== undefined && canUpgradeToYearly(heldSubscription);
  const canExtend = heldSubscription !== undefined && canExtendYear(heldSubscription);
  const bestYearlyOffer =
    offersForLine(offers, strategy.id, "Yearly").sort(
      (a, b) =>
        savingOnLine(b, strategy.yearlyPriceUsd) -
        savingOnLine(a, strategy.yearlyPriceUsd),
    )[0] ?? null;
  const firstYearUsd =
    strategy.yearlyPriceUsd -
    (bestYearlyOffer ? savingOnLine(bestYearlyOffer, strategy.yearlyPriceUsd) : 0);
  const yearlySaving =
    (heldSubscription?.priceAtSubscriptionUsd ?? 0) * 12 - firstYearUsd;

  const upgrade = () => {
    startUpgrade({
      strategyId: strategy.id,
      name: strategy.publicName,
      yearlyPriceUsd: strategy.yearlyPriceUsd,
      offerId: bestYearlyOffer?.id ?? null,
    });
    navigate(routePaths.cart);
  };

  const handleUpgrade = () => {
    const alreadyInCart = cartUpgrade?.strategyId === strategy.id;
    if (alreadyInCart) {
      navigate(routePaths.cart);
      return;
    }

    const cartIsEmpty =
      strategyItems.length === 0 &&
      cartMembershipItems.length === 0 &&
      cartAddOnItems.length === 0 &&
      cartUpgrade === null &&
      cartExtension === null;
    if (!cartIsEmpty) {
      setAskingToEmptyCart(true);
      return;
    }

    upgrade();
  };

  // Deliberately a copy of the subscription page's Extend button - change both together.
  const extend = () => {
    startExtension({
      strategyId: strategy.id,
      name: strategy.publicName,
      yearlyPriceUsd: heldSubscription?.priceAtSubscriptionUsd ?? strategy.yearlyPriceUsd,
      offerId: bestYearlyOffer?.id ?? null,
    });
    navigate(routePaths.cart);
  };

  const handleExtend = () => {
    if (cartExtension?.strategyId === strategy.id) {
      navigate(routePaths.cart);
      return;
    }

    const cartIsEmpty =
      strategyItems.length === 0 &&
      cartMembershipItems.length === 0 &&
      cartAddOnItems.length === 0 &&
      cartUpgrade === null &&
      cartExtension === null;
    if (!cartIsEmpty) {
      setAskingToEmptyCartForExtension(true);
      return;
    }

    extend();
  };

  const handleCycleChange = (cycle: BillingCycle) => {
    if (cartItem) {
      updateStrategyBillingCycle(strategy.id, cycle, priceForCycle(strategy, cycle));
    } else {
      setPendingCycle(cycle);
    }
  };

  const actionButton = subscriptionStateUnavailable ? (
    <CButton color="secondary" disabled>
      Checking subscription
    </CButton>
  ) : subscribed ? (
    canUpgrade ? (
      <div className="d-flex flex-column gap-2">
        <CButton color="primary" onClick={handleUpgrade}>
          Upgrade to yearly
          {yearlySaving > 0 && ` · save ${formatPriceUsd(yearlySaving)}`}
        </CButton>
        {bestYearlyOffer && (
          <div className="small text-body-secondary text-center">
            with {bestYearlyOffer.publicName}
          </div>
        )}
      </div>
    ) : canExtend ? (
      <div className="d-flex flex-column gap-2">
        <CButton color="primary" onClick={handleExtend}>
          Extend your year
        </CButton>
        {bestYearlyOffer && (
          <div className="small text-body-secondary text-center">
            with {bestYearlyOffer.publicName}
          </div>
        )}
      </div>
    ) : (
      <CButton color="secondary" disabled>
        Already subscribed
      </CButton>
    )
  ) : includedIn.length > 0 ? (
    <CButton color="secondary" disabled>
      {includedIn.length === 1
        ? `Included in your ${includedIn[0].tierName} membership`
        : "Included in your memberships"}
    </CButton>
  ) : inCart ? (
    <CButton
      color="danger"
      variant="outline"
      onClick={() => removeStrategy(strategy.id)}
    >
      Remove from cart
    </CButton>
  ) : (
    <CButton
      color="primary"
      disabled={!isPublic}
      onClick={() => {
        const item: StrategyCartItem = {
          strategyId: strategy.id,
          name: strategy.publicName,
          billingCycle: selectedCycle,
          priceUsd: selectedPrice,
        };
        if (cartUpgrade !== null || cartExtension !== null) {
          setHeldBack(item);
          return;
        }

        addStrategy(item);
        showSuccess(`${strategy.publicName} added to cart.`);
      }}
    >
      Add to cart
    </CButton>
  );

  return (
    <div>
      <StrategySideTabs
        sides={sides}
        currentId={strategy.id}
        cycle={selectedCycle}
        browsing={!fromMyStrategies}
      />
      <CCard style={hasSideTabs ? { borderTopLeftRadius: 0 } : undefined}>
        <CCardBody>
          {/* Laid out as the subscription page is: the strategy, its facts and its figures on the
              left, buying it on the right, so a strategy reads the same before and after it's held. */}
          <CRow xs={{ gutter: 4 }} className="pb-4 border-bottom">
            <CCol md={8}>
              {!isPublic && (
                <small className="text-body-secondary d-block mb-2">
                  Not available for new subscriptions.
                </small>
              )}
              <h2 className="mb-2">{strategy.publicName}</h2>
              <p
                className="text-body-secondary mb-0"
                style={{ fontSize: "1rem" }}
              >
                {strategy.description}
              </p>

              <div className="mt-4 pt-4 border-top strategy-facts">
                <StrategyTraitFacts
                  strategy={strategy}
                  showDirection={!hasSideTabs}
                  showMarket
                />
              </div>

              <div className="mt-4">
                <StrategyFigures strategy={strategy} threeAcross />
              </div>
            </CCol>

            <CCol md={4}>
              <div className="subscription-column d-flex flex-column gap-3">
                <div
                  className="text-body-secondary text-uppercase small fw-semibold"
                  style={{ letterSpacing: "0.08em" }}
                >
                  Billing cycle
                </div>
                <CButtonGroup className="btn-group-pill">
                  <CButton
                    color="primary"
                    variant={selectedCycle === "Monthly" ? undefined : "outline"}
                    onClick={() => handleCycleChange("Monthly")}
                  >
                    Monthly
                  </CButton>
                  <CButton
                    color="primary"
                    variant={selectedCycle === "Yearly" ? undefined : "outline"}
                    onClick={() => handleCycleChange("Yearly")}
                  >
                    Yearly
                    {savings > 0 && (
                      <span className="ms-1 small">· save {savings}%</span>
                    )}
                  </CButton>
                </CButtonGroup>
                <div style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.1 }}>
                  <StrategyPriceHeadline
                    free={isFreeStrategy(strategy)}
                    amountUsd={selectedPrice}
                    cycle={selectedCycle}
                  />
                </div>
                <div className="d-grid gap-2">{actionButton}</div>
                {heldOtherSide && !subscribed && (
                  <p className="small text-body-secondary mb-0">
                    You hold {heldOtherSideWords}. It carries on until you end it from its tab.
                  </p>
                )}
                <StrategyColumnLinks>
                  {/* Current or past: a cancelled subscription is still someone who ran it, and the
                      API enforces the same rule rather than trusting this. */}
                  <StrategyFeedbackLinks
                    strategy={strategy}
                    hasSubscribed={subscriptionItems.some(
                      (subscription) => subscription.strategy.id === strategy.id,
                    )}
                  />
                  <StrategyColumnLink
                    icon={<CIcon icon={cilBook} />}
                    to={helpArticlePath("reading-a-strategy-page")}
                  >
                    How to read this page
                  </StrategyColumnLink>
                </StrategyColumnLinks>
              </div>
            </CCol>
          </CRow>

          <TradingStrategyInsights strategy={strategy} periodInNote showFigures={false} />

          <div className="d-flex gap-2 mt-5 pt-3">
            <CButton
              color="secondary"
              onClick={() =>
                navigate(fromMyStrategies ? routePaths.myStrategies : routePaths.strategies)
              }
            >
              <CIcon icon={cilArrowLeft} className="me-1" />
              {fromMyStrategies
                ? t("common:buttons.backToMyStrategies")
                : t("common:buttons.backToStrategies")}
            </CButton>
          </div>
        </CCardBody>
      </CCard>

      <CartHoldsUpgradeDialog item={heldBack} onClose={() => setHeldBack(null)} />
      <ExtendOnItsOwnDialog
        strategyName={strategy.publicName}
        visible={askingToEmptyCartForExtension}
        onKeep={() => setAskingToEmptyCartForExtension(false)}
        onEmpty={() => {
          setAskingToEmptyCartForExtension(false);
          extend();
        }}
      />
      <UpgradeOnItsOwnDialog
        strategyName={strategy.publicName}
        visible={askingToEmptyCart}
        onKeep={() => setAskingToEmptyCart(false)}
        onEmpty={() => {
          setAskingToEmptyCart(false);
          upgrade();
        }}
      />
    </div>
  );
}
